import { useState, useEffect, useCallback } from 'react';
import { Match } from '@/lib/database';

interface HomepageBundle {
  liveMatches: Match[];
  upcomingMatches: Match[];
  featuredMatch?: Match;
  matchCounts: {
    total: number;
    live: number;
    today: number;
    tomorrow: number;
  };
}

interface UseHomepageResult {
  // Main data
  bundle: HomepageBundle | null;
  
  // Individual components for easy access
  liveMatches: Match[];
  upcomingMatches: Match[];
  featuredMatch?: Match;
  matchCounts: {
    total: number;
    live: number;
    today: number;
    tomorrow: number;
  };
  
  // Loading states
  loading: boolean;
  error: string | null;
  fromCache: boolean;
  
  // Performance info
  performance?: {
    total_time: number;
    live_count: number;
    upcoming_count: number;
    has_featured: boolean;
  };
  
  // Actions
  refetch: () => void;
}

// Client-side cache for homepage data (sync with ISR 5 minutes)
const CLIENT_CACHE = new Map<string, { data: HomepageBundle; timestamp: number }>();
const CLIENT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes - matches ISR revalidate

export function useHomepage(): UseHomepageResult {
  const [bundle, setBundle] = useState<HomepageBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [performance, setPerformance] = useState<any>(undefined);

  const fetchHomepageData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const startTime = Date.now();
      
      // Check client-side cache first
      const cacheKey = 'homepage-data';
      const cached = CLIENT_CACHE.get(cacheKey);
      
      if (cached && (Date.now() - cached.timestamp) < CLIENT_CACHE_TTL) {
        setBundle(cached.data);
        setFromCache(true);
        setPerformance({ 
          total_time: Date.now() - startTime, 
          from_client_cache: true 
        });
        setLoading(false);
        return;
      }

      const response = await fetch('/api/homepage-data', {
        signal: AbortSignal.timeout(30000) // 30 second timeout for production
      });
      
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch homepage data');
      }

      const bundleData = result.data as HomepageBundle;
      setBundle(bundleData);
      setFromCache(result._fromCache || false);
      setPerformance(result.performance);

      // Cache in client for 2 minutes
      CLIENT_CACHE.set(cacheKey, {
        data: bundleData,
        timestamp: Date.now()
      });

      // Clean up old cache entries (keep max 5 entries)
      if (CLIENT_CACHE.size > 5) {
        const entries = Array.from(CLIENT_CACHE.entries());
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        
        // Remove oldest entries
        for (let i = 0; i < entries.length - 5; i++) {
          CLIENT_CACHE.delete(entries[i][0]);
        }
      }

    } catch (err) {
      console.error('Homepage fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => {
    // Clear client cache and refetch
    CLIENT_CACHE.delete('homepage-data');
    fetchHomepageData();
  }, [fetchHomepageData]);

  useEffect(() => {
    fetchHomepageData();
  }, [fetchHomepageData]);

  // Individual components for easy access
  const individualComponents = {
    liveMatches: bundle?.liveMatches || [],
    upcomingMatches: bundle?.upcomingMatches || [],
    featuredMatch: bundle?.featuredMatch,
    matchCounts: bundle?.matchCounts || {
      total: 0,
      live: 0,
      today: 0,
      tomorrow: 0
    }
  };

  return {
    bundle,
    ...individualComponents,
    loading,
    error,
    fromCache,
    performance,
    refetch
  };
}

// Prefetch homepage data for faster loading
export function prefetchHomepageData() {
  const cacheKey = 'homepage-data';
  const cached = CLIENT_CACHE.get(cacheKey);
  
  // Only prefetch if not in cache or cache is old
  if (!cached || (Date.now() - cached.timestamp) > CLIENT_CACHE_TTL) {
    fetch('/api/homepage-data')
      .then(response => response.json())
      .then(result => {
        if (result.success) {
          CLIENT_CACHE.set(cacheKey, {
            data: result.data,
            timestamp: Date.now()
          });
          }
      })
      .catch(error => {
        console.warn('Homepage prefetch failed:', error);
      });
  }
}
